import React from 'react';
import PropTypes from 'prop-types';
import { IconDateAdd } from '@medflex/components/stories/Icons';
import { useTranslation } from 'react-i18next';
import Button from '@medflex/components/stories/Button';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import Tooltip from '@medflex/components/stories/Tooltip';
import Link from '@medflex/components/stories/Link';
import useUserProfile from '../../hooks/useUserProfile';
import useSessionRoles from '../../hooks/useSessionRoles';
import Participants from './Participants';
import getParticipantsWithoutOrganizer from '../../../utils/getParticipantsWithoutOrganizer';
import useModal from '../../hooks/useModal';
import { MODALS } from '../../hooks/useIsModal';
import useAddressBookContacts from '../../hooks/useAddressBookContacts';

const Details = ({ id, title, participants }) => {
  const { isOrganizer, isModerator, moderator } = useSessionRoles(participants);
  const { userProfile } = useUserProfile();
  const participantsWithoutOrganizer = getParticipantsWithoutOrganizer(participants);
  const { t } = useTranslation('global');
  const { open } = useModal();
  const { findOneAddressBookContactByUserId } = useAddressBookContacts();

  const handleClickCreate = () => {
    open({
      modal: MODALS.sessionsCreate,
      mode: participantsWithoutOrganizer.length > 2 ? 'group' : 'single',
      sessionId: id,
      copy: true,
    });
  };

  const handleDetailClicked = () => {
    open({
      modal: MODALS.sessionsDetail,
      sessionId: id,
    });
  };

  const moderatorUrl =
    (moderator?.userId &&
      findOneAddressBookContactByUserId(moderator.userId)?.id &&
      `/chat/${findOneAddressBookContactByUserId(moderator.userId).id}`) ||
    null;

  return (
    <div className="flex items-center mb-1 lg:mb-0">
      {(isOrganizer || isModerator) && (
        <Tooltip title={t('sessions.SessionsListModule.Details.tooltip')}>
          <div>
            <button
              type="button"
              className="absolute top-0 right-0 p-2 lg:p-0 lg:static self-start"
              onClick={handleClickCreate}
            >
              <IconDateAdd className="text-slate-500" size="6" />
            </button>
          </div>
        </Tooltip>
      )}
      <div className="lg:ml-2 min-w-0">
        <Button
          kind="link"
          className="w-full hover:no-underline hover:text-blue-600 font-semibold"
          onClick={handleDetailClicked}
        >
          <span className="truncate">{title}</span>
        </Button>
        <Participants items={participants} />
        <div className="text-slate-600 font-normal">
          {moderator && moderator.userId !== userProfile.id && (
            <span>
              {t('sessions.SessionsListModule.Details.moderated_by')}
              {moderatorUrl ? (
                <Link to={moderatorUrl} className="font-normal text-slate-600 hover:text-slate-600">
                  {getFullNameWithTitle({
                    id: moderator.id,
                    firstName: moderator.firstName,
                    lastName: moderator.lastName,
                  })}
                </Link>
              ) : (
                getFullNameWithTitle({
                  id: moderator.id,
                  firstName: moderator.firstName,
                  lastName: moderator.lastName,
                })
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

Details.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.oneOf(['moderator', 'organizer'])),
    }),
  ),
};

export default Details;
